/**
 * Helpers for the voice "custom treatment" flow — an ad-hoc treatment the manager
 * builds on the spot from individual products + doses (no pest, no configured recipe).
 *
 * The dosing math is delegated to `treatmentDosing.ts` (the single source of truth),
 * so a custom product resolves to an absolute amount identically to a recipe product.
 */
import { resolveProductAmount, classifyUnit, isLiquidStock, isSolidStock } from "@/lib/treatmentDosing";

/* La misma tabla que SWAP en supabase/functions/_shared/voice/dose.ts (paridad en
   src/__tests__/voz/dose.test.ts). La llave va en minúsculas: «cc» es «mL». */
const LIQUID_TO_SOLID: Record<string, string> = {
  "l": "kg", "ml": "g", "cc": "g", "l/ha": "kg/ha", "l/1000m²": "kg/1000m²", "l/1000m2": "kg/1000m²",
  "ml/100l": "g/100L", "cc/100l": "g/100L",
};
const SOLID_TO_LIQUID: Record<string, string> = {
  "kg": "L", "g": "mL", "kg/ha": "L/ha", "kg/1000m²": "L/1000m²", "kg/1000m2": "L/1000m²", "g/100l": "mL/100L",
};

/**
 * La unidad dictada, en la forma del producto: «3 litros» de un producto que se
 * lleva en kg son 3 kg, y «300 cc por 100 litros» de un polvo son 300 g/100L.
 *
 * Lo pidió Pablo (29-09-2026): con la unidad dicha, litros contra kilos, la
 * tarjeta salía en litros, la cantidad en 0 (UNIT_PRODUCT_MISMATCH) y no dejaba
 * guardar. En el campo se dice «litros» de todo; manda la unidad del inventario.
 * Solo cambia la unidad entre líquido y sólido; el número se deja tal cual. Un
 * producto por unidades, o una unidad que no se reconoce, se deja como vino.
 */
export function doseUnitForProduct(doseUnit: string | null | undefined, stockUnit: string | null | undefined): string | null {
  if (!doseUnit) return null;
  const key = doseUnit.trim().toLowerCase().replace(/\s+/g, "");
  if (isSolidStock(stockUnit || "") && LIQUID_TO_SOLID[key]) return LIQUID_TO_SOLID[key];
  if (isLiquidStock(stockUnit || "") && SOLID_TO_LIQUID[key]) return SOLID_TO_LIQUID[key];
  return doseUnit;
}

/** A product line as parsed from voice, before the user confirms/edits it. */
export interface ParsedProduct {
  spoken_name?: string | null;
  product_id?: string | null;
  product_name?: string | null;
  variant_label?: string | null;
  dose_value?: number | null;
  dose_unit?: string | null;
  /** Where the dose came from when it was not said: "habitual_usuario" (this person's
   *  usual dose) or "habitual" (the company's). Drives the notice under the dose. */
  dose_source?: string | null;
  /** Populated when the spoken name matched more than one inventory product. */
  ambiguous_candidates?: { id: string; name: string; variant_label: string | null }[];
  /** True when the spoken name also matches a configured treatment name. */
  name_collision?: boolean;
}

/**
 * Re-resolve a rate (value + unit) into its absolute amount + derived dose fields.
 * Mirrors `resolveProductRate` in TaskFormDialog so both paths compute identically.
 */
export function resolveCustomAmount(
  rateValue: number,
  rateUnit: string,
  stockUnit: string,
  areaM2: number,
  caldoL: number | null,
): { amount: number; unit: string; dose_value: number | null; dose_unit: string | null; amount_per_ha: number | null } {
  const r = resolveProductAmount({ value: rateValue, unit: rateUnit, areaM2, caldoL, stockUnit });
  const basis = classifyUnit(rateUnit);
  return {
    amount: r.error ? 0 : r.amount,
    unit: r.amountUnit,
    dose_value: basis === "per_caldo" ? rateValue : null,
    dose_unit: basis === "per_caldo" ? rateUnit : null,
    amount_per_ha: basis === "per_area" ? rateValue : null,
  };
}

/** Minimal inventory row shape the builder needs to resolve a product. */
export interface InventoryRow {
  id: string;
  product_name: string;
  unit: string;
  quantity: number;
  variant_label?: string | null;
  units_per_package?: number | null;
}

/**
 * Pure builder: turn voice-parsed products into editable resolved product lines
 * (with computed amounts) + the leftover ones the user must still disambiguate.
 * Shared by the panel's initial enrichment and the confirmation's mode-toggle
 * re-derivation, so both produce identical results with no DB round-trip.
 */
export function buildCustomProductsFromParsed(
  parsed: ParsedProduct[],
  invById: Map<string, InventoryRow>,
  blockIds: string[],
  areaM2: number,
  caldoL: number | null,
): { resolved: any[]; unresolved: ParsedProduct[] } {
  const resolved: any[] = [];
  const unresolved: ParsedProduct[] = [];
  for (const p of parsed || []) {
    if (!p.product_id || !invById.has(p.product_id)) { unresolved.push(p); continue; }
    const inv = invById.get(p.product_id)!;
    const stock_unit = inv.unit || "L";
    const rate_unit = doseUnitForProduct(p.dose_unit, stock_unit) || stock_unit;
    const rate_value = p.dose_value != null ? p.dose_value : 0;
    const calc = resolveCustomAmount(rate_value, rate_unit, stock_unit, areaM2, caldoL);
    resolved.push({
      product_id: p.product_id,
      product_name: inv.product_name || p.product_name || p.spoken_name || "",
      variant_label: inv.variant_label ?? p.variant_label ?? null,
      amount: calc.amount,
      unit: calc.unit,
      stock: inv.quantity ?? 0,
      is_from_treatment: false,
      rate_value,
      rate_unit,
      stock_unit,
      dose_value: calc.dose_value,
      dose_unit: calc.dose_unit,
      amount_per_ha: calc.amount_per_ha,
      units_per_package: inv.units_per_package ?? null,
      target_blocks: blockIds,
      // Solo si NO se dijo: la tarjeta avisa de que es la dosis habitual (DV16).
      ...(p.dose_value != null && (p.dose_source === "habitual_usuario" || p.dose_source === "habitual") ? { dose_source: p.dose_source } : {}),
    });
  }
  return { resolved, unresolved };
}
